/**
 * Lesson viewer page hero — same clay/violet language as LearnerPageHero,
 * with lesson-specific layout (meta row, path under LESSON, passed accent).
 */

import React, { useCallback, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { BookOpen, CheckCircle2 } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../hooks/useTheme';
import { darkenColor } from '../../utils/colorUtils';
import { mirrorLinearGradientAngle, mirrorRtlDecor } from '../../utils/mirrorRtlDecor';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';
import {
  DASHBOARD_BOOK_CARD_WATERMARK,
  dashboardBookCardWatermarkCornerClass,
} from '../dashboard/dashboardCoursePanelStyles';
import { PersianText } from '../shared/PersianText';

const HERO_INNER_GRADIENT_LIGHT: [string, string, string] = ['#f5f3ff', '#fae8ff', '#eef2ff'];
const HERO_INNER_GRADIENT_DARK: [string, string, string] = ['#2e1065', '#581c87', '#3730a3'];

const HERO_INNER_RADIAL_A =
  'pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(167,139,250,0.18),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(139,92,246,0.12),transparent_58%)]';

const HERO_INNER_RADIAL_B =
  'pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(192,132,252,0.1),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(124,58,237,0.08),transparent_55%)]';

const HERO_BLOB_TOP = 'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/35 dark:bg-white/10';
const HERO_BLOB_BOTTOM =
  'pointer-events-none absolute -bottom-0 -left-8 h-28 w-28 rounded-full bg-violet-900/[0.06] dark:bg-black/25';

const HERO_CLAY_FRAME =
  'border border-violet-300/95 bg-violet-100/95 shadow-[0_8px_32px_rgba(139,92,246,0.14),inset_0_2px_4px_rgba(255,255,255,0.6),inset_0_-2px_4px_rgba(124,58,237,0.08)] ring-1 ring-violet-200/90 dark:border-violet-600/65 dark:bg-violet-950/80 dark:shadow-[0_8px_32px_rgba(0,0,0,0.35),inset_0_2px_4px_rgba(255,255,255,0.07),inset_0_-2px_4px_rgba(0,0,0,0.28)] dark:ring-violet-800/50';

export interface LessonPageHeroProps {
  eyebrow?: string;
  /** Line under the eyebrow and above the title (for example the tag name). */
  eyebrowDetail?: string;
  /** When true, eyebrow keeps natural casing (e.g. tag names). */
  eyebrowPlain?: boolean;
  title: string;
  description?: string | null;
  /** Smaller, tighter, justified description under the title (lesson and tag lesson). */
  compactDescription?: boolean;
  pathParts?: string[];
  pathAriaLabel?: string;
  /** Leading + watermark icon (defaults to BookOpen). */
  icon?: LucideIcon;
  isPassed?: boolean;
  passedAriaLabel?: string;
  /** Rating / view / favorite row. */
  topBar?: React.ReactNode;
  /** Optional control under the leading icon (e.g. back). */
  belowIcon?: React.ReactNode;
  /** Optional trailing control (e.g. back to dashboard). */
  action?: React.ReactNode;
  /** `underBack` stacks the action under the back control. Otherwise it sits under the description. */
  actionPlacement?: 'belowDescription' | 'underBack';
  /** Optional node under the icon column (e.g. English-locked hint). */
  underIconExtra?: React.ReactNode;
  /** Slightly smaller logo on lesson, tag, and tag lesson viewers. */
  compactChrome?: boolean;
  /** Optional progress bar under the description (quiz / practice / tag). */
  progress?: React.ReactNode;
  /** Optional footer under the main row (guides, browse tabs). */
  footer?: React.ReactNode;
  /** Lesson image shown as a translucent, lightly blurred background (opacity 0.15). */
  backgroundImageUrl?: string | null;
  className?: string;
  tourId?: string;
}

export function LessonPageHero({
  eyebrow,
  eyebrowDetail,
  eyebrowPlain = false,
  title,
  description,
  compactDescription = false,
  pathParts = [],
  pathAriaLabel,
  icon: Icon = BookOpen,
  isPassed = false,
  passedAriaLabel,
  topBar,
  belowIcon,
  action,
  actionPlacement = 'belowDescription',
  underIconExtra,
  compactChrome = false,
  progress,
  footer,
  backgroundImageUrl,
  className = '',
  tourId,
}: LessonPageHeroProps) {
  const { direction } = useAppLocale();
  const { isDark } = useTheme();
  const isRtl = direction === 'rtl';
  const rtlDecor = (cls: string) => mirrorRtlDecor(cls, isRtl);
  const trimmedDescription = description?.trim() ?? '';
  const actionUnderBack = actionPlacement === 'underBack' && action != null;
  const heroImageSrc = backgroundImageUrl?.trim()
    ? normalizeAppHostedUploadUrl(backgroundImageUrl.trim())
    : null;

  const makeGradient = useCallback(
    (...stops: string[]) => {
      if (!stops.length) return undefined;
      const adjusted = stops.map((stop) => (isDark ? darkenColor(stop, 0.35) : stop));
      const angle = mirrorLinearGradientAngle(135, isRtl);
      return `linear-gradient(${angle}deg, ${adjusted.join(', ')})`;
    },
    [isDark, isRtl]
  );

  const heroInnerGradient = useMemo(() => {
    const stops = isDark ? HERO_INNER_GRADIENT_DARK : HERO_INNER_GRADIENT_LIGHT;
    const ordered = isRtl ? ([...stops].reverse() as typeof stops) : stops;
    return makeGradient(...ordered);
  }, [isDark, isRtl, makeGradient]);

  return (
    <header
      className={`lesson-page-hero group relative flex flex-col overflow-hidden rounded-2xl p-1 sm:p-1.5 ${HERO_CLAY_FRAME} ${className}`}
      dir={direction}
      aria-label={title}
      {...(tourId ? { 'data-tour-id': tourId } : {})}
    >
      <div
        className="relative isolate flex flex-1 flex-col overflow-hidden rounded-xl"
        style={{ background: heroInnerGradient }}
      >
        {heroImageSrc ? (
          <img
            src={heroImageSrc}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0 h-full w-full scale-[1.02] object-cover opacity-[0.15] blur-[2px]"
          />
        ) : null}
        <div className={rtlDecor(HERO_BLOB_TOP)} aria-hidden />
        <div className={rtlDecor(HERO_BLOB_BOTTOM)} aria-hidden />
        <div className={rtlDecor(HERO_INNER_RADIAL_A)} aria-hidden />
        <div className={rtlDecor(HERO_INNER_RADIAL_B)} aria-hidden />
        <Icon
          className={`${DASHBOARD_BOOK_CARD_WATERMARK} text-violet-400 dark:text-violet-300 ${dashboardBookCardWatermarkCornerClass(isRtl)}`}
          strokeWidth={1.25}
          aria-hidden
        />
        <div className="relative z-[1] flex flex-col gap-3 px-4 pb-4 pt-2 md:gap-3.5 md:px-5 md:pb-5 md:pt-2.5">
          {topBar ? (
            <div
              dir={direction}
              className="relative z-[1] flex shrink-0 items-center justify-between gap-2 border-b border-violet-200/70 pb-1.5 dark:border-violet-700/50"
            >
              {topBar}
            </div>
          ) : null}

          <div className="relative z-[1] flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-3 text-start">
              <div className="flex shrink-0 flex-col items-center gap-1.5">
                <div className="relative">
                  <div
                    className={rtlDecor(
                      compactChrome
                        ? 'flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-indigo-600 shadow-md ring-2 ring-white/70 dark:from-violet-600 dark:via-fuchsia-600 dark:to-indigo-700 dark:ring-white/10 md:h-10 md:w-10'
                        : 'flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-indigo-600 shadow-md ring-2 ring-white/70 dark:from-violet-600 dark:via-fuchsia-600 dark:to-indigo-700 dark:ring-white/10 md:h-12 md:w-12'
                    )}
                  >
                    <Icon className={compactChrome ? 'h-4 w-4 text-white md:h-5 md:w-5' : 'h-5 w-5 text-white md:h-6 md:w-6'} aria-hidden />
                  </div>
                  {isPassed ? (
                    <span
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md ring-2 ring-white dark:bg-emerald-600 dark:ring-violet-950/80"
                      aria-label={passedAriaLabel}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    </span>
                  ) : null}
                </div>
                {belowIcon || actionUnderBack ? (
                  <div className="flex w-full flex-col items-center gap-0.5">
                    {belowIcon ? <div className="flex w-full justify-center">{belowIcon}</div> : null}
                    {actionUnderBack ? <div className="flex w-full justify-center">{action}</div> : null}
                  </div>
                ) : null}
                {underIconExtra ? <div className="flex w-full justify-center">{underIconExtra}</div> : null}
              </div>

              <div className="min-w-0 flex-1">
                {eyebrow ? (
                  <p
                    className={
                      eyebrowPlain
                        ? 'text-xs font-semibold tracking-tight text-violet-700/90 dark:text-violet-300/90 md:text-sm'
                        : 'text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700/90 dark:text-violet-300/90 md:text-[11px]'
                    }
                  >
                    {eyebrow}
                  </p>
                ) : null}
                {eyebrowDetail ? (
                  <p className="text-xs font-semibold tracking-tight text-violet-800/85 dark:text-violet-200/90 md:text-sm">
                    {eyebrowDetail}
                  </p>
                ) : null}
                {pathParts.length > 0 ? (
                  <nav aria-label={pathAriaLabel} className={eyebrow ? 'mt-0.5' : undefined}>
                    <ol className="flex flex-wrap items-center gap-1 text-[10px] leading-snug text-slate-500 dark:text-slate-400 md:text-[11px]">
                      {pathParts.map((part, index, parts) => (
                        <li key={`${part}-${index}`} className="inline-flex items-center gap-1">
                          {index > 0 ? (
                            <span className="text-slate-400 dark:text-slate-500" aria-hidden>
                              ›
                            </span>
                          ) : null}
                          <span
                            className={
                              index === parts.length - 1
                                ? 'font-medium text-slate-600 dark:text-slate-300'
                                : undefined
                            }
                          >
                            {part}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </nav>
                ) : null}
                <h1 className="mt-1 text-xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white md:text-2xl">
                  {title}
                </h1>
                {trimmedDescription ? (
                  compactDescription ? (
                    <p className="mt-1 text-justify text-[11px] leading-snug text-slate-600 dark:text-slate-400 md:text-xs">
                      <PersianText
                        inline={false}
                        className="block w-full"
                        options={{ textAlign: 'justify', lineHeight: '1.35', fontSize: '1em' }}
                      >
                        {trimmedDescription}
                      </PersianText>
                    </p>
                  ) : (
                    <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400 md:text-sm">
                      <PersianText>{trimmedDescription}</PersianText>
                    </p>
                  )
                ) : null}
                {progress ? <div className="mt-2 w-full">{progress}</div> : null}
                {!actionUnderBack && action ? <div className="mt-3 w-full md:w-auto">{action}</div> : null}
              </div>
            </div>
          </div>

          {footer ? <div className="relative z-[1] flex flex-col gap-3">{footer}</div> : null}
        </div>
      </div>
    </header>
  );
}

export default LessonPageHero;
